import React from "react";

import { stylex } from "./stylex";
import { useHoverState } from "../hooks";

interface PressableState {
  focused: boolean;
  pressed: boolean;
  hovered: boolean;
}

interface PressableProps {
  children?: React.ReactNode | ((state: PressableState) => React.ReactNode);
  testID?: string;
  style?:
    | React.CSSProperties
    | ((state: PressableState) => React.CSSProperties);
  disabled?: boolean;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
  onPressIn?: () => void;
  onPressOut?: () => void;
  onPress?: () => void;
  className?: string;
}

export const Pressable = React.forwardRef<HTMLButtonElement, PressableProps>(
  (props: PressableProps, ref) => {
    const {
      children,
      onHoverIn = () => {},
      onHoverOut = () => {},
      onPressIn = () => {},
      onPressOut = () => {},
      onPress = () => {},
      testID,
      style,
      disabled,
      className,
    } = props;
    const isHoverEnabled = useHoverState();
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);
    const [isPressed, setIsPressed] = React.useState(false);
    const [showHover, setShowHover] = React.useState(true);

    const handleFocus = React.useCallback(() => {
      setIsFocused(true);
    }, []);

    const handleBlur = React.useCallback(() => {
      setIsFocused(false);
    }, []);

    const handlePressIn = React.useCallback(() => {
      setIsPressed(true);
      setShowHover(false);

      onPressIn();
    }, [onPressIn]);

    const handlePressOut = React.useCallback(() => {
      setIsPressed(false);
      setShowHover(true);
      onPressOut();
    }, [onPressOut]);

    const handleHoverIn = React.useCallback(() => {
      if (isHoverEnabled && !isHovered) {
        setIsHovered(true);
      }
      onHoverIn();
    }, [isHovered, isHoverEnabled, onHoverIn]);

    const handleHoverOut = React.useCallback(() => {
      if (isHovered) {
        setIsHovered(false);
      }
      onHoverOut();
    }, [isHovered, onHoverOut]);

    const state = {
      focused: isFocused,
      hovered: showHover && isHovered,
      pressed: isPressed,
    };

    return (
      <button
        className={className}
        ref={ref}
        data-testid={testID}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={onPress}
        disabled={!!disabled}
        onMouseEnter={handleHoverIn}
        onMouseLeave={handleHoverOut}
        onMouseDown={handlePressIn}
        onMouseUp={handlePressOut}
        onTouchStart={handlePressIn}
        onTouchEnd={handlePressOut}
        style={{
          ...styles("base"),
          ...(disabled && styles("disabled")),
          ...(typeof style === "function" ? style(state) : style),
        }}
      >
        {typeof children === "function" ? children(state) : children}
      </button>
    );
  }
);

const styles = stylex.create({
  base: {
    display: "flex",
    flexDirection: "column",
    boxShadow: "none",
    boxSizing: "border-box",
    alignItems: "stretch",
    backgroundColor: "transparent",
    backgroundImage: "none",
    padding: 0,
    margin: 0,
    borderWidth: 0,
    borderStyle: "solid",
    borderColor: "transparent",
    cursor: "pointer",
    textDecoration: "none",
    outline: "none",
  },
  disabled: {
    cursor: "auto",
  },
});
